import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  INVITATION_TYPE_MAP,
  LEGACY_HIDDEN_TYPES,
  PERSON_INVITATION_TYPE_CATALOG,
  PersonInvitationTypeFilterOption,
  REGISTRATION_TYPE_CATALOG,
  REGISTRATION_TYPE_LEGEND,
  RegistrationTypeCode,
  RegistrationTypeFilterOption,
  RegistrationTypeLegendItem,
} from '../interface/registration-type.interface';
import { AccessCardType, AccessTypeMacro, PersonInvitationType } from '../interface/frequentAccess.inteface';
import { sortByTranslatedLabel, sortCodesByTranslatedLabel } from '../shared/utils/sort-by-translated-label.util';
import { DataService } from './data.service';
import { WhiteLabelService } from './white-label.service';

interface VisitsWhiteLabelConfig {
  registrationTypes?: RegistrationTypeCode[];
  registrationTypesByRole?: Partial<Record<string, RegistrationTypeCode[]>>;
}

/** White-label block under `dashboard.frequentAccess` (filters + create-access options). */
interface FrequentAccessWhiteLabelConfig {
  personInvitationTypes?: string[];
}

/** Select/filter option for door-opening "Tipo de visitante". */
export interface VisitorTypeSelectOption {
  value: string;
  display: string;
}

/** Default "Tipo de registro" options when creating frequent access (admin / non-proprietor). */
export const DEFAULT_CREATE_ACCESS_INVITATION_TYPES: readonly string[] = [
  AccessCardType.COLLABORATOR,
  AccessCardType.PATIENT,
  AccessCardType.VISIT,
];

/**
 * VDS-3260 / VDS-3267: MTS baseline (proprietor + customized) for FA create / visitor type.
 * Order is applied later via translated labels (not this array order).
 */
export const PROPRIETOR_CREATE_ACCESS_INVITATION_TYPES: readonly string[] = [
  PersonInvitationType.EMPLOYEE,
  PersonInvitationType.PROVIDER,
  PersonInvitationType.VISIT,
];

/** Legacy macro catalog for door-opening create/filters (non-customized). */
export const DEFAULT_MACRO_VISITOR_TYPE_OPTIONS: readonly VisitorTypeSelectOption[] = [
  { value: AccessTypeMacro.COLLABORATOR, display: 'frequentAccess.accessTypes.collaborator' },
  { value: AccessTypeMacro.DELIVERY, display: 'frequentAccess.accessTypes.delivery' },
  { value: AccessTypeMacro.GUEST, display: 'frequentAccess.accessTypes.guest' },
  { value: AccessTypeMacro.PACKAGE, display: 'frequentAccess.accessTypes.package' },
  { value: AccessTypeMacro.PROVIDER, display: 'frequentAccess.accessTypes.provider' },
  { value: AccessTypeMacro.VISIT, display: 'frequentAccess.accessTypes.visit' },
];

const ACCESS_TYPE_I18N_PREFIX = 'frequentAccess.accessTypes.';

/**
 * Centraliza la visibilidad de tipos de filtro según white label del edificio
 * y fallback legacy por pantalla. También ordena opciones por label traducido.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationTypeOptionsService {
  constructor(
    private dataService: DataService,
    private whiteLabelService: WhiteLabelService,
    private translate: TranslateService,
  ) {}

  getVisibleTypes(applyLegacyHide = true): RegistrationTypeCode[] {
    const building = this.dataService.getBuilding();
    const configured = this.resolveRegistrationTypes(
      this.getVisitsConfig(building),
    );

    if (configured?.length) {
      return this.filterRegistrationCatalog(configured);
    }

    if (this.shouldApplyLegacyHide(building, applyLegacyHide)) {
      return this.filterRegistrationCatalog(
        REGISTRATION_TYPE_CATALOG.map((item) => item.value).filter(
          (type) => !LEGACY_HIDDEN_TYPES.includes(type),
        ),
      );
    }

    return this.filterRegistrationCatalog(
      REGISTRATION_TYPE_CATALOG.map((item) => item.value),
    );
  }

  isTypeVisible(type: string, applyLegacyHide = true): boolean {
    return this.getVisibleTypes(applyLegacyHide).includes(type as RegistrationTypeCode);
  }

  /**
   * Filter options for visits "Tipo de registro", sorted by translated label.
   * Rebuild on language change so alphabetical order stays correct.
   */
  buildFilterOptions(applyLegacyHide = true): RegistrationTypeFilterOption[] {
    const visible = new Set(this.getVisibleTypes(applyLegacyHide));
    const options = REGISTRATION_TYPE_CATALOG.filter((item) => visible.has(item.value));
    return sortByTranslatedLabel(options, (item) => item.labelKey, this.translate);
  }

  /**
   * Color legend for Visitas — same visibility as the registration-type filter
   * (WL `visits.registrationTypes`), in the fixed legend order (not alphabetical).
   */
  buildLegendItems(applyLegacyHide = true): RegistrationTypeLegendItem[] {
    const visible = new Set(this.getVisibleTypes(applyLegacyHide));
    return REGISTRATION_TYPE_LEGEND.filter((item) => visible.has(item.value));
  }

  getVisiblePersonInvitationTypes(): string[] {
    const configured = this.getFrequentAccessConfig(
      this.dataService.getBuilding(),
    )?.personInvitationTypes;

    if (Array.isArray(configured) && configured.length) {
      const allowed = new Set(configured);
      return PERSON_INVITATION_TYPE_CATALOG.filter((item) =>
        allowed.has(item.value),
      ).map((item) => item.value);
    }

    return PERSON_INVITATION_TYPE_CATALOG.map((item) => item.value);
  }

  buildFrequentAccessFilterOptions(): PersonInvitationTypeFilterOption[] {
    const visible = new Set(this.getVisiblePersonInvitationTypes());
    const options = PERSON_INVITATION_TYPE_CATALOG.filter((item) =>
      visible.has(item.value),
    );
    return sortByTranslatedLabel(options, (item) => item.labelKey, this.translate);
  }

  /**
   * Codes for create-access "Tipo de registro", sorted by translated label.
   *
   * Layers:
   * 1. Role baseline (proprietor/customized → employee/provider/visit; else collaborator/patient/visit)
   * 2. If WL `frequentAccess.personInvitationTypes` is set → intersect with that list
   *    (WL stays the customization gate; unexpected extras mean review the WL config)
   */
  getCreateAccessInvitationTypes(): string[] {
    const baseline = this.usesMtsVisitorTypeBaseline()
      ? [...PROPRIETOR_CREATE_ACCESS_INVITATION_TYPES]
      : [...DEFAULT_CREATE_ACCESS_INVITATION_TYPES];

    const wlTypes = this.getFrequentAccessConfig(
      this.dataService.getBuilding(),
    )?.personInvitationTypes;

    let codes = baseline;
    if (Array.isArray(wlTypes) && wlTypes.length) {
      const allowed = new Set(wlTypes);
      const intersected = baseline.filter((type) => allowed.has(type));
      // Keep baseline if misconfigured WL would empty the select.
      if (intersected.length) {
        codes = intersected;
      }
    }

    return sortCodesByTranslatedLabel(codes, ACCESS_TYPE_I18N_PREFIX, this.translate);
  }

  /**
   * VDS-3267: options for door-opening "Tipo de visitante" (create-access-v2 + filters).
   * Customized en edificio NO-macro (MTS/business) → Empleado / Proveedor / Visitante.
   * Macrocomunidad (padre) y resto → catálogo macro legacy (colaborador, delivery, …).
   */
  buildDoorOpeningVisitorTypeOptions(): VisitorTypeSelectOption[] {
    if (this.shouldUseMtsVisitorTypeOptions()) {
      const codes = sortCodesByTranslatedLabel(
        [...PROPRIETOR_CREATE_ACCESS_INVITATION_TYPES],
        ACCESS_TYPE_I18N_PREFIX,
        this.translate,
      );
      return codes.map((code) => ({
        value: code,
        display: `${ACCESS_TYPE_I18N_PREFIX}${code}`,
      }));
    }

    return sortByTranslatedLabel(
      [...DEFAULT_MACRO_VISITOR_TYPE_OPTIONS],
      (item) => item.display,
      this.translate,
    );
  }

  /**
   * Proprietor puro, o customized fuera de macrocomunidad padre (MTS business/tradicional).
   * No aplicar baseline MTS en macro: conserva collaborator/delivery/guest/package/….
   */
  usesMtsVisitorTypeBaseline(): boolean {
    return this.dataService.isProprietorOnly() || this.shouldUseMtsVisitorTypeOptions();
  }

  /** Customized en contexto MTS (no padre de macrocomunidad). */
  private shouldUseMtsVisitorTypeOptions(): boolean {
    return this.dataService.isCustomized() && !this.dataService.getBuildingIsMacroFather();
  }

  private getVisitsConfig(building: any): VisitsWhiteLabelConfig | undefined {
    return building?.config?.whiteLabel?.projects?.dashboard?.visits;
  }

  private getFrequentAccessConfig(
    building: any,
  ): FrequentAccessWhiteLabelConfig | undefined {
    return building?.config?.whiteLabel?.projects?.dashboard?.frequentAccess;
  }

  private resolveRegistrationTypes(
    visitsConfig: VisitsWhiteLabelConfig | undefined,
  ): RegistrationTypeCode[] | undefined {
    if (
      Array.isArray(visitsConfig?.registrationTypes) &&
      visitsConfig.registrationTypes.length
    ) {
      return visitsConfig.registrationTypes;
    }

    const byRole = visitsConfig?.registrationTypesByRole;
    if (!byRole || !Object.keys(byRole).length) {
      return undefined;
    }

    const allowed = new Set<RegistrationTypeCode>();
    Object.values(byRole).forEach((types) => {
      if (!Array.isArray(types)) return;
      types.forEach((type) => allowed.add(type as RegistrationTypeCode));
    });

    return allowed.size ? Array.from(allowed) : undefined;
  }

  private shouldApplyLegacyHide(building: any, applyLegacyHide: boolean): boolean {
    if (!applyLegacyHide) {
      return false;
    }
    return this.whiteLabelService.shouldHideBasicRegistrationTypes(building);
  }

  private filterRegistrationCatalog(
    types: RegistrationTypeCode[],
  ): RegistrationTypeCode[] {
    const building = this.dataService.getBuilding();
    const invitation = building?.config?.invitation;
    const catalogOrder = REGISTRATION_TYPE_CATALOG.map((item) => item.value);

    const filtered = types.filter((type) => {
      const invitationKey = INVITATION_TYPE_MAP[type];
      if (!invitationKey) return true;
      if (!invitation?.enable) return false;
      return !!invitation[invitationKey];
    });

    return catalogOrder.filter((type) => filtered.includes(type));
  }
}
